import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Stack,
  FormControl,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Button,
  Snackbar,
  Alert,
  Fade,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { API_URL } from "../../../Config/api"; // Adjust path if necessary

// --- Premium Modal Styling ---
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 500 }, // Responsive width
  bgcolor: "#ffffff",
  borderRadius: "16px", // Modern rounded corners
  boxShadow: "0px 12px 40px rgba(0, 0, 0, 0.12)", // Soft, deep premium shadow
  outline: "none",
  overflow: "hidden",
};

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fafafa",
    transition: "all 0.3s ease",
    "&:hover fieldset": {
      borderColor: "rgb(173, 219, 177)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgb(173, 219, 177)",
      borderWidth: "2px",
    },
  },
};

const LongTermFormModal = ({ open, onClose, eventID, accessToken }) => {
  const [yesNo, setYesNo] = useState(2);
  const [notifyby, setNotifyby] = useState(null);
  const [notifyname, setNotifyname] = useState("");
  const [roleList, setRoleList] = useState({ data: [] });
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Snackbar States
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // 1. Fetch Existing Long Term Form Data when Modal Opens
  useEffect(() => {
    if (open && eventID) {
      fetch(`${API_URL}/web/ongoing_service_get/?eve_id=${eventID}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
        .then((res) => res.json())
        .then((resData) => {
          const dataArr = resData?.data || [];
          if (dataArr.length > 0) {
            const latest = dataArr[dataArr.length - 1];
            setYesNo(latest.long_term_form || 2);
            setNotifyby(latest.notify_by || null);
            setNotifyname(latest.notify_name || "");

            // Check if already submitted
            if (
              latest.long_term_form !== null &&
              latest.notify_by !== null &&
              latest.notify_name
            ) {
              setIsSubmitted(true);
            } else {
              setIsSubmitted(false);
            }
          } else {
            setYesNo(2);
            setNotifyby(null);
            setNotifyname("");
            setIsSubmitted(false);
          }
        })
        .catch((err) => console.error("Prefetch Error:", err));
    }
  }, [open, eventID, accessToken]);

  // 2. Fetch Role List based on Notify By (Admin/Professional)
  useEffect(() => {
    if (notifyby) {
      fetch(`${API_URL}/web/role/${notifyby}/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.role === "professional" && data.data) {
            const combinedList = Object.values(data.data).flat();
            setRoleList({ role: data.role, data: combinedList });
          } else {
            setRoleList(data);
          }
        })
        .catch((err) => console.error("API Error fetching roles:", err));
    }
  }, [notifyby, accessToken]);

  // 3. Handle Form Submission
  const handleSave = () => {
    if (!eventID) {
      setSnackbarMessage("Event ID missing!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const payload = {
      notify_by: notifyby,
      notify_name: notifyname,
      eve_id: eventID,
      long_term_form: yesNo,
    };

    fetch(`${API_URL}/web/ongoing_service/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.message === "Data saved successfully") {
          setSnackbarMessage("Data saved successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          setIsSubmitted(true);
        } else {
          setSnackbarMessage(data.message || "Something went wrong!");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      })
      .catch((err) => {
        setSnackbarMessage("API Error — please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  return (
    <>
      <Modal open={open} onClose={onClose} closeAfterTransition>
        <Fade in={open}>
          <Box sx={modalStyle}>
            {/* --- Premium Header --- */}
            <Box
              sx={{
                background: "rgb(173, 219, 177)",
                px: 3,
                py: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: "#ffffff",
                  letterSpacing: "0.5px",
                  textShadow: "0px 1px 2px rgba(0,0,0,0.1)",
                }}
              >
                Long Term Form
              </Typography>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  color: "#FFFFFF",
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* --- Modal Body --- */}
            <Box sx={{ p: 4 }}>
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#555", mb: 1, fontWeight: 500 }}
                  >
                    Continue Long Term Care?
                  </Typography>
                  <FormControl fullWidth size="medium" sx={inputStyle}>
                    <Select
                      value={yesNo}
                      onChange={(e) => setYesNo(Number(e.target.value))}
                      disabled={isSubmitted}
                      displayEmpty
                    >
                      <MenuItem value={1}>Yes</MenuItem>
                      <MenuItem value={2}>No</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {yesNo === 1 && (
                  <Fade in={yesNo === 1}>
                    <Box>
                      <Divider sx={{ mb: 2, opacity: 0.6 }} />

                      <Typography
                        variant="subtitle2"
                        sx={{ color: "#555", mb: 0.5, fontWeight: 500 }}
                      >
                        Notify Person Role
                      </Typography>
                      <RadioGroup
                        row
                        value={notifyby || ""}
                        onChange={(e) => setNotifyby(Number(e.target.value))}
                        sx={{ mb: 2 }}
                      >
                        <FormControlLabel
                          value={1}
                          control={
                            <Radio
                              disabled={isSubmitted}
                              sx={{
                                color: "rgb(173, 219, 177)",
                                "&.Mui-checked": {
                                  color: "rgb(130, 190, 135)",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{ fontSize: "14px", color: "#444" }}
                            >
                              Admin
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value={2}
                          control={
                            <Radio
                              disabled={isSubmitted}
                              sx={{
                                color: "rgb(173, 219, 177)",
                                "&.Mui-checked": {
                                  color: "rgb(130, 190, 135)",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{ fontSize: "14px", color: "#444" }}
                            >
                              Professional
                            </Typography>
                          }
                        />
                      </RadioGroup>

                      <Typography
                        variant="subtitle2"
                        sx={{ color: "#555", mb: 1, fontWeight: 500 }}
                      >
                        Select Specific Name
                      </Typography>
                      <FormControl fullWidth size="medium" sx={inputStyle}>
                        <Select
                          value={notifyname || ""}
                          onChange={(e) => setNotifyname(e.target.value)}
                          disabled={isSubmitted}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            <em>Choose a person</em>
                          </MenuItem>
                          {roleList?.data?.map((item) => (
                            <MenuItem
                              key={item.clg_ref_id || item.srv_prof_id}
                              value={item.clg_ref_id || item.srv_prof_id}
                            >
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Fade>
                )}

                {/* --- Action Area --- */}
                <Box sx={{ mt: 2 }}>
                  {!isSubmitted ? (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSave}
                      sx={{
                        background: "rgb(173, 219, 177)",
                        color: "#2c3e50",
                        fontWeight: 600,
                        fontSize: "15px",
                        py: 1.2,
                        borderRadius: "10px",
                        boxShadow: "0px 4px 12px rgba(173, 219, 177, 0.4)",
                        textTransform: "none",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "rgb(143, 199, 147)",
                          boxShadow: "0px 6px 16px rgba(143, 199, 147, 0.5)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      Submit Form
                    </Button>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        py: 1.5,
                        borderRadius: "10px",
                        color: "#166534",
                        boxShadow: "inset 0px 2px 4px rgba(0,0,0,0.02)",
                      }}
                    >
                      <CheckCircleOutlineIcon sx={{ mr: 1, fontSize: 22 }} />
                      <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>
                        Form Already Submitted
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          variant="filled"
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            fontWeight: 500,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LongTermFormModal;
